<template>
  <div class="">
    <div v-if="label" class="label">{{ label }}</div>
    <ValidationProvider
      :name="$attrs.label"
      :rules="rules"
      v-slot="{ errors }"
      :vid="vid"
    >
      <v-select
        class="custom-select"
        v-model="innerValue"
        :error-messages="errors"
        v-bind="$attrs"
        v-on="$listeners"
        solo
        dense
        hide-details="auto"
        :items="items"
        :item-text="itemText"
        :item-value="itemValue"
        :disabled="disabled"
      >
        <!-- Override the default list item appearance -->
        <template v-slot:item="{ item, attrs, on }">
          <v-list-item
            v-bind="attrs"
            v-on="on"
            :class="{ 'custom-active-item': isItemSelected(item) }"
          >
            <v-list-item-content>
              <v-list-item-title>{{ getItemText(item) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-select>
    </ValidationProvider>
  </div>
</template>

<script>
import { ValidationProvider } from "vee-validate";

export default {
  components: {
    ValidationProvider,
  },
  props: {
    rules: {
      type: [Object, String],
      default: "",
    },
    items: {
      type: Array,
      default: () => [],
    },
    value: {
      type: null,
    },
    vid: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    width: {
      type: Number,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    returnValue: {
      type: Boolean,
      default: false,
    },
    itemText: {
      type: String,
      default: "name",
    },
    itemValue: {
      type: String,
      default: "value",
    },
    selectedValue: {
      type: [String, Number],
      default: null,
    },
  },
  data: () => ({
    innerValue: "",
  }),
  watch: {
    // Handles internal model changes
    innerValue(newVal) {
      if (this.returnValue) {
        this.$emit("input", this.resolveReturnedValue(newVal));
      } else {
        this.$emit("input", newVal);
      }
    },
    // Handles external model changes
    value(newVal) {
      this.innerValue = newVal;
    },
    // Watch for items changes
    items(newItems) {
      if (this.selectedValue !== null && this.selectedValue !== undefined) {
        const foundItem = newItems.find(
          (item) =>
            String(this.getItemValue(item)) === String(this.selectedValue)
        );
        if (foundItem) {
          this.innerValue = this.getItemValue(foundItem);
        }
      }
    },
  },
  methods: {
    emitChange() {
      this.$emit("newValueAfterChange", this.innerValue);
    },
    getItemText(item) {
      if (item && typeof item === "object") {
        return item[this.itemText] ?? item.name ?? item.value ?? "";
      }
      return item;
    },
    getItemValue(item) {
      if (item && typeof item === "object") {
        return item[this.itemValue] ?? item.value ?? item.id;
      }
      return item;
    },
    resolveReturnedValue(newVal) {
      if (newVal === null || newVal === undefined || newVal === "") {
        return newVal;
      }

      if (typeof newVal === "object") {
        return this.getItemValue(newVal);
      }

      const foundItem = this.items.find((item) => {
        const itemValue = this.getItemValue(item);
        const itemText = this.getItemText(item);
        return (
          String(itemValue) === String(newVal) ||
          String(itemText) === String(newVal)
        );
      });

      if (foundItem) {
        return this.getItemValue(foundItem);
      }

      return newVal;
    },
    isItemSelected(item) {
      const itemValue = this.getItemValue(item);
      const itemText = this.getItemText(item);

      if (this.innerValue && typeof this.innerValue === "object") {
        return (
          String(this.getItemValue(this.innerValue)) === String(itemValue)
        );
      }

      return (
        String(this.innerValue) === String(itemValue) ||
        String(this.innerValue) === String(itemText)
      );
    },
  },
  created() {
    if (this.value !== null && this.value !== undefined && this.value !== "") {
      this.innerValue = this.value;
    } else if (
      this.selectedValue !== null &&
      this.selectedValue !== undefined &&
      this.selectedValue !== ""
    ) {
      const foundItem = this.items.find(
        (item) => String(this.getItemValue(item)) === String(this.selectedValue)
      );
      if (foundItem) {
        this.innerValue = this.getItemValue(foundItem);
      }
    }
  },
};
</script>

<style scoped>
.custom-select {
  margin: 8px 0 20px 0;
  box-shadow: none !important;
  border: none !important;
}

.custom-select >>> .v-input {
  border-radius: 12px;
  background-color: white !important;
  box-shadow: none !important;
  border: none !important;
}

.custom-select >>> .v-input.v-input--solo,
.custom-select >>> .v-input.v-input--solo.v-input--is-focused {
  box-shadow: none !important;
  border: none !important;
}

.custom-select >>> .v-input__control {
  border-radius: 12px;
  padding: 0 !important;
  box-shadow: none !important;
  background-color: transparent !important;
}

.custom-select >>> .v-select__slot {
  padding: 0 15px !important;
  color: #6d6963;
  font-size: 12px;
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.custom-select >>> .v-select__selection {
  color: #6d6963;
  font-size: 12px;
}

.custom-select >>> .v-label {
  font-weight: 400;
  font-size: 12px;
  color: #6d6963 !important;
  opacity: 1 !important;
  transform: none !important;
  box-shadow: none !important;
  border: none !important;
}

.label {
  font-weight: 400;
  font-size: 12px;
  color: #6d6963;
  margin-bottom: 0;
}

.custom-select >>> .v-icon {
  color: #6d6963 !important;
}

.custom-select >>> .v-list-item {
  font-size: 12px;
  color: #6d6963;
}

/* Style for the dropdown itself */
.custom-select >>> .v-menu__content {
  border-radius: 8px;
}

/* Custom class for active items (used in the template slot) */
.custom-active-item,
.custom-select >>> .v-list-item--active {
  background-color: rgba(109, 105, 99, 0.1) !important;
  color: #6d6963 !important;
}

/* Override the default blue active background */
.custom-select >>> .v-list-item--active::before,
.custom-select >>> .theme--light.v-list-item--active::before {
  opacity: 0 !important; 
}

/* Override blue styling in text and selection */
.custom-select >>> .primary--text,
.custom-select >>> .v-list-item__action--select .v-icon {
  color: #6d6963 !important;
  caret-color: #6d6963 !important;
}

/* Override the checkbox color if it appears */
.custom-select >>> .v-input--selection-controls__input .v-icon {
  color: #6d6963 !important;
}

/* Override hover state */
.custom-select >>> .v-list-item:hover {
  background-color: rgba(109, 105, 99, 0.05) !important;
}
</style>
